import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "phoneInput",
    "phoneHelp",
    "submitButton",
    "pinInput",
    "countryCode",
    "resendLink",
    "resendCountdown",
  ];

  static values = {
    resendWait: { type: Number, default: 60 },
  };

  connect() {
    this.timeoutId = null;
    this.ellipsisIntervalId = null;
    this.resendIntervalId = null;
    this.resendRemainingSeconds = null;
    this.isSubmitting = false;

    // Initialize phone validation if we have both phone input AND phone help
    if (this.hasPhoneInputTarget && this.hasPhoneHelpTarget) {
      this.validatePhone();
    }

    // Note: We don't disable the PIN button on connect anymore
    // The button will only be disabled after clicking (loading state)

    // Store original button state
    if (this.hasSubmitButtonTarget) {
      this.originalButtonText = this.submitButtonTarget.value;
      this.originalButtonClasses = this.submitButtonTarget.className;
    }

    // Bind form submit handler
    if (this.element.tagName === "FORM") {
      this.element.addEventListener("submit", this.handleFormSubmit.bind(this));
    }

    this.startResendCountdown();
  }

  disconnect() {
    // Clean up timers when controller is removed
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    if (this.ellipsisIntervalId) {
      clearInterval(this.ellipsisIntervalId);
    }
    if (this.resendIntervalId) {
      clearInterval(this.resendIntervalId);
    }
  }

  validatePhone() {
    // Safety check: only validate if we have all required elements
    if (
      !this.hasPhoneInputTarget ||
      !this.hasPhoneHelpTarget ||
      !this.hasSubmitButtonTarget
    ) {
      return;
    }

    const phone = this.phoneInputTarget.value;
    const isValid = this.isValidPhoneNumber(phone);

    this.submitButtonTarget.disabled = !isValid;
    this.phoneHelpTarget.textContent = isValid
      ? this.phoneHelpTarget.dataset.validMessage
      : this.phoneHelpTarget.dataset.invalidMessage;
  }

  validatePin() {
    // Optional: You can add visual feedback here if needed
    // but don't disable the button
    const pin = this.pinInputTarget.value;
    const isValid = /^\d{4}$/.test(pin);

    // We no longer disable the button based on PIN validation
    // The form's native validation (required, pattern) will handle this
  }

  countryChanged(event) {
    // Update the hidden country code field when country is changed
    if (this.hasCountryCodeTarget) {
      this.countryCodeTarget.value = event.detail.countryCode;
    }
  }

  isValidPhoneNumber(phone) {
    // Remove any non-digit characters
    const digits = phone.replace(/\D/g, "");

    // Check if intlTelInput is initialized
    if (!this.phoneInputTarget.intlTelInput) {
      // If intlTelInput is not available, use basic validation
      return digits.length >= 8 && digits.length <= 15;
    }

    // Get the selected country code from intl-tel-input
    const iti = this.phoneInputTarget.intlTelInput;
    const countryData = iti.getSelectedCountryData();
    const countryCode = countryData.iso2.toUpperCase();

    // Chilean mobile numbers:
    // - Must start with 9
    // - Must be 9 digits total (including the 9)
    if (countryCode === "CL") {
      if (digits.length === 9 && digits.startsWith("9")) {
        return true;
      }
      return false;
    }

    // For other countries, use a more general validation
    // You might want to add specific validation for other countries
    return digits.length >= 8 && digits.length <= 15;
  }

  handleFormSubmit(event) {
    // Prevent multiple submissions
    if (this.isSubmitting) {
      event.preventDefault();
      return;
    }

    // Mark as submitting
    this.isSubmitting = true;

    // Set loading state immediately for visual feedback
    // Use setTimeout to ensure the form can still be submitted
    setTimeout(() => {
      this.setLoadingState();
    }, 0);

    // Set timeout to reset button if no response after 30 seconds
    this.timeoutId = setTimeout(() => {
      this.resetButton();
      this.isSubmitting = false;
      // Show error message if timeout occurs
      const timeoutMessage =
        this.submitButtonTarget.dataset.timeoutMessage ||
        "La operación tomó demasiado tiempo. Por favor intenta de nuevo.";
      alert(timeoutMessage);
    }, 30000);

    // Let the form submit naturally (don't prevent default)
  }

  preventResendBeforeReady(event) {
    if (this.resendLinkTarget.getAttribute("aria-disabled") !== "true") return;

    event.preventDefault();
  }

  startResendCountdown() {
    if (!this.hasResendLinkTarget || !this.hasResendCountdownTarget) return;

    this.resendRemainingSeconds = Math.max(this.resendWaitValue, 0);
    this.disableResendLink();

    if (this.resendRemainingSeconds <= 0) {
      this.enableResendLink();
      return;
    }

    this.updateResendCountdown();

    this.resendIntervalId = setInterval(() => {
      this.resendRemainingSeconds = Math.max(
        this.resendRemainingSeconds - 1,
        0
      );

      if (this.resendRemainingSeconds <= 0) {
        this.enableResendLink();
        return;
      }

      this.updateResendCountdown();
    }, 1000);
  }

  disableResendLink() {
    this.resendLinkTarget.classList.add("terra-carrier-link--disabled");
    this.resendLinkTarget.setAttribute("aria-disabled", "true");
    this.resendLinkTarget.setAttribute("tabindex", "-1");
    this.resendCountdownTarget.hidden = false;
  }

  enableResendLink() {
    this.resendLinkTarget.classList.remove("terra-carrier-link--disabled");
    this.resendLinkTarget.removeAttribute("aria-disabled");
    this.resendLinkTarget.removeAttribute("tabindex");
    this.resendCountdownTarget.hidden = true;

    if (this.resendIntervalId) {
      clearInterval(this.resendIntervalId);
      this.resendIntervalId = null;
    }
  }

  updateResendCountdown() {
    const unit = this.resendRemainingSeconds === 1 ? "segundo" : "segundos";
    this.resendCountdownTarget.textContent =
      `Podrás reenviar el código en ${this.resendRemainingSeconds} ${unit}.`;
  }

  setLoadingState() {
    if (!this.hasSubmitButtonTarget) return;

    // Disable the button
    this.submitButtonTarget.disabled = true;

    // Get the loading text from data attribute or use default
    const loadingText =
      this.submitButtonTarget.dataset.loadingText || "Por favor espere";

    // Add loading classes to make button subdued while keeping original color
    this.submitButtonTarget.classList.add(
      "opacity-60",
      "cursor-not-allowed",
      "pointer-events-none"
    );

    // Start animated ellipsis
    let dotCount = 0;
    this.submitButtonTarget.value = loadingText;

    this.ellipsisIntervalId = setInterval(() => {
      dotCount = (dotCount + 1) % 4;
      const dots = ".".repeat(dotCount);
      this.submitButtonTarget.value = loadingText + dots;
    }, 500);
  }

  resetButton() {
    if (!this.hasSubmitButtonTarget) return;

    // Clear timers
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.ellipsisIntervalId) {
      clearInterval(this.ellipsisIntervalId);
      this.ellipsisIntervalId = null;
    }

    // Reset submitting flag
    this.isSubmitting = false;

    // Restore original button state
    this.submitButtonTarget.disabled = false;
    this.submitButtonTarget.value = this.originalButtonText;
    this.submitButtonTarget.classList.remove(
      "opacity-60",
      "cursor-not-allowed",
      "pointer-events-none"
    );
  }
}
